import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createCourse,
  createLanguageLesson,
  createModule,
  createTopic,
  deleteLanguageLesson,
  deleteModule,
  deleteTopic,
  getLanguages,
  getLanguageLessons,
  getModules,
  getTopics,
  updateCourse,
  updateLanguageLesson,
  updateModule,
  updateTopic,
} from "../api";
import "./createcourse.css";

const CreateCourse = () => {
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [courseId, setCourseId] = useState(null);
  const [courseDraft, setCourseDraft] = useState({
    title: "",
    description: "",
    visibility: "public",
    image: null,
  });

  const [modules, setModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [moduleDraft, setModuleDraft] = useState({ title: "", introduction: "" });

  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState(null);
  const [topicDraft, setTopicDraft] = useState({ title: "", description: "" });

  const [lessons, setLessons] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [lessonDraft, setLessonDraft] = useState({
    language_id: "",
    topic_content: "",
    topic_image: null,
    audio: null,
    video: null,
    document: null,
  });

  const [activeSection, setActiveSection] = useState("course");

  const selectedModule = useMemo(
    () => modules.find((m) => m.id === selectedModuleId) || null,
    [modules, selectedModuleId],
  );

  const selectedTopic = useMemo(
    () => topics.find((t) => t.id === selectedTopicId) || null,
    [topics, selectedTopicId],
  );

  useEffect(() => {
    const loadLanguages = async () => {
      const items = await getLanguages();
      setLanguages(items);
    };

    loadLanguages();
  }, []);

  const refreshModules = async (id) => {
    const items = await getModules(id);
    setModules(items);
    if (!items.find((m) => m.id === selectedModuleId)) {
      setSelectedModuleId(items[0]?.id || null);
      setTopics([]);
      setSelectedTopicId(null);
      setLessons([]);
      setSelectedLessonId(null);
    }
  };

  const refreshTopics = async (moduleId) => {
    const items = await getTopics(moduleId);
    setTopics(items);
    if (!items.find((t) => t.id === selectedTopicId)) {
      setSelectedTopicId(items[0]?.id || null);
      setLessons([]);
      setSelectedLessonId(null);
    }
  };

  const refreshLessons = async (topicId) => {
    const items = await getLanguageLessons(topicId);
    setLessons(items);
    if (!items.find((l) => l.id === selectedLessonId)) {
      setSelectedLessonId(items[0]?.id || null);
    }
  };

  const buildCoursePayload = () => {
    if (courseDraft.image instanceof File) {
      const fd = new FormData();
      fd.append("title", courseDraft.title.trim());
      fd.append("description", (courseDraft.description || "").trim());
      fd.append("is_published", courseDraft.visibility === "public" ? "1" : "0");
      fd.append("category_id", "1");
      fd.append("job_title_id", "1");
      fd.append("image_file", courseDraft.image);
      return fd;
    }

    return {
      title: courseDraft.title.trim(),
      description: (courseDraft.description || "").trim(),
      is_published: courseDraft.visibility === "public",
      category_id: 1,
      job_title_id: 1,
      image: typeof courseDraft.image === "string" ? courseDraft.image : null,
    };
  };

  const handleSaveCourse = async () => {
    if (!courseDraft.title.trim()) {
      setError("Course title is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = buildCoursePayload();

      if (!courseId) {
        const res = await createCourse(payload);
        const createdId = res?.data?.id || res?.id;
        if (!createdId) throw new Error("Course created but ID was not returned.");
        setCourseId(createdId);
        await refreshModules(createdId);
      } else {
        await updateCourse(courseId, payload);
      }
    } catch (err) {
      setError(err?.message || "Failed to save course.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddModule = async () => {
    if (!courseId) {
      setError("Save the course first before adding modules.");
      return;
    }
    if (!moduleDraft.title.trim()) {
      setError("Module title is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await createModule({
        course_id: courseId,
        title: moduleDraft.title.trim(),
        introduction: (moduleDraft.introduction || "").trim(),
      });
      setModuleDraft({ title: "", introduction: "" });
      await refreshModules(courseId);
    } catch (err) {
      setError(err?.message || "Failed to add module.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateModule = async () => {
    if (!selectedModuleId) return;
    try {
      setSaving(true);
      setError("");
      await updateModule(selectedModuleId, {
        title: selectedModule?.title || "",
        introduction: selectedModule?.introduction || "",
      });
      await refreshModules(courseId);
    } catch (err) {
      setError(err?.message || "Failed to update module.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = async () => {
    if (!selectedModuleId) return;
    if (!window.confirm("Delete this module and its topics?")) return;

    try {
      setSaving(true);
      setError("");
      await deleteModule(selectedModuleId);
      await refreshModules(courseId);
    } catch (err) {
      setError(err?.message || "Failed to delete module.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTopic = async () => {
    if (!selectedModuleId) {
      setError("Select a module first.");
      return;
    }
    if (!topicDraft.title.trim()) {
      setError("Topic title is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await createTopic({
        module_id: selectedModuleId,
        title: topicDraft.title.trim(),
        description: (topicDraft.description || "").trim(),
      });
      setTopicDraft({ title: "", description: "" });
      await refreshTopics(selectedModuleId);
    } catch (err) {
      setError(err?.message || "Failed to add topic.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTopic = async () => {
    if (!selectedTopicId) return;
    try {
      setSaving(true);
      setError("");
      await updateTopic(selectedTopicId, {
        title: selectedTopic?.title || "",
        description: selectedTopic?.description || "",
      });
      await refreshTopics(selectedModuleId);
    } catch (err) {
      setError(err?.message || "Failed to update topic.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (!selectedTopicId) return;
    if (!window.confirm("Delete this topic and its lessons?")) return;

    try {
      setSaving(true);
      setError("");
      await deleteTopic(selectedTopicId);
      await refreshTopics(selectedModuleId);
    } catch (err) {
      setError(err?.message || "Failed to delete topic.");
    } finally {
      setSaving(false);
    }
  };

  const buildLessonPayload = () => {
    const fd = new FormData();
    fd.append("topic_id", String(selectedTopicId));
    fd.append("topic_name", selectedTopic?.title || "Untitled Topic");
    fd.append("language_id", String(lessonDraft.language_id || ""));
    fd.append("topic_content", lessonDraft.topic_content || "");

    if (lessonDraft.topic_image instanceof File) fd.append("topic_image", lessonDraft.topic_image);
    if (lessonDraft.audio instanceof File) fd.append("audio", lessonDraft.audio);
    if (lessonDraft.video instanceof File) fd.append("video", lessonDraft.video);
    if (lessonDraft.document instanceof File) fd.append("document", lessonDraft.document);

    return fd;
  };

  const handleAddOrUpdateLesson = async () => {
    if (!selectedTopicId) {
      setError("Select a topic first.");
      return;
    }
    if (!lessonDraft.language_id) {
      setError("Language ID is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      const payload = buildLessonPayload();

      if (selectedLessonId) {
        await updateLanguageLesson(selectedLessonId, payload);
      } else {
        await createLanguageLesson(payload);
      }

      setLessonDraft({
        language_id: "",
        topic_content: "",
        topic_image: null,
        audio: null,
        video: null,
        document: null,
      });
      setSelectedLessonId(null);
      await refreshLessons(selectedTopicId);
    } catch (err) {
      setError(err?.message || "Failed to save lesson.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditLesson = (lesson) => {
    setSelectedLessonId(lesson.id);
    setLessonDraft({
      language_id: lesson.language_id ? String(lesson.language_id) : "",
      topic_content: lesson.topic_content || "",
      topic_image: null,
      audio: null,
      video: null,
      document: null,
    });
  };

  const handleDeleteLesson = async (id) => {
    if (!window.confirm("Delete this language lesson?")) return;

    try {
      setSaving(true);
      setError("");
      await deleteLanguageLesson(id);
      await refreshLessons(selectedTopicId);
      if (selectedLessonId === id) {
        setSelectedLessonId(null);
      }
    } catch (err) {
      setError(err?.message || "Failed to delete lesson.");
    } finally {
      setSaving(false);
    }
  };

  const jumpToSection = (section) => setActiveSection(section);

  return (
    <div className="course-edit-page">
      <div className="edit-header">
        <h1>Create Course (Step-by-Step)</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => navigate("/courses")}>Back to Courses</button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="internal-nav">
        <button
          type="button"
          className={`internal-nav-btn ${activeSection === "course" ? "active" : ""}`}
          onClick={() => jumpToSection("course")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 7l9-4 9 4-9 4-9-4z" />
            <path d="M3 17l9 4 9-4" />
            <path d="M3 12l9 4 9-4" />
          </svg>
          Course
        </button>
        <button
          type="button"
          className={`internal-nav-btn ${activeSection === "modules" ? "active" : ""}`}
          onClick={() => jumpToSection("modules")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
          </svg>
          Modules
        </button>
        <button
          type="button"
          className={`internal-nav-btn ${activeSection === "topics" ? "active" : ""}`}
          onClick={() => jumpToSection("topics")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 6h13" />
            <path d="M8 12h13" />
            <path d="M8 18h13" />
            <path d="M3 6h.01" />
            <path d="M3 12h.01" />
            <path d="M3 18h.01" />
          </svg>
          Topics
        </button>
        <button
          type="button"
          className={`internal-nav-btn ${activeSection === "lessons" ? "active" : ""}`}
          onClick={() => jumpToSection("lessons")}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 20h9" />
            <path d="M12 4h9" />
            <path d="M12 12h9" />
            <path d="M3 6h6v4H3z" />
            <path d="M3 14h6v4H3z" />
          </svg>
          Language Lessons
        </button>
      </div>

      <div className="edit-content single-panel">
        {activeSection === "course" && (
        <div className="edit-column">
          <h2>1. Course</h2>
          <div className="form-group">
            <label>Title</label>
            <input className="form-input" value={courseDraft.title} onChange={(e) => setCourseDraft((p) => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="form-textarea" rows={4} value={courseDraft.description} onChange={(e) => setCourseDraft((p) => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Image</label>
            <label className="icon-upload-btn" title="Upload course image">
              <input className="hidden-input" type="file" accept="image/*" onChange={(e) => setCourseDraft((p) => ({ ...p, image: e.target.files?.[0] || null }))} />
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span>{courseDraft.image?.name ? "Change Image" : "Upload Image"}</span>
            </label>
            {courseDraft.image?.name ? <p className="form-hint">{courseDraft.image.name}</p> : null}
          </div>
          <div className="radio-group">
            <label className="radio">
              <input type="radio" name="visibility" value="private" checked={courseDraft.visibility === "private"} onChange={(e) => setCourseDraft((p) => ({ ...p, visibility: e.target.value }))} />
              <span>Private</span>
            </label>
            <label className="radio">
              <input type="radio" name="visibility" value="public" checked={courseDraft.visibility === "public"} onChange={(e) => setCourseDraft((p) => ({ ...p, visibility: e.target.value }))} />
              <span>Public</span>
            </label>
          </div>
          <button className="btn btn-primary w-full" onClick={handleSaveCourse} disabled={saving}>{courseId ? "Update Course" : "Save Course"}</button>
          {courseId && <p className="form-hint">Course ID: {courseId}</p>}

        </div>
        )}

        {activeSection === "modules" && (
        <div className="edit-column">
          <h2>2. Modules</h2>
          <div className="form-group">
            <label>Module Title</label>
            <input className="form-input" value={moduleDraft.title} onChange={(e) => setModuleDraft((p) => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Introduction</label>
            <textarea className="form-textarea" rows={3} value={moduleDraft.introduction} onChange={(e) => setModuleDraft((p) => ({ ...p, introduction: e.target.value }))} />
          </div>
          <button className="btn btn-outline w-full" onClick={handleAddModule} disabled={saving}>Add Module</button>

          <div className="module-list" style={{ marginTop: 12 }}>
            {modules.map((m) => (
              <div key={m.id} className={`module-item ${selectedModuleId === m.id ? "selected" : ""}`} onClick={async () => { setSelectedModuleId(m.id); await refreshTopics(m.id); }}>
                <span className="module-name">{m.title}</span>
              </div>
            ))}
          </div>

          {selectedModule ? (
            <>
              <div className="form-group">
                <label>Edit Selected Module Title</label>
                <input className="form-input" value={selectedModule.title || ""} onChange={(e) => setModules((prev) => prev.map((m) => (m.id === selectedModule.id ? { ...m, title: e.target.value } : m)))} />
              </div>
              <div className="form-group">
                <label>Edit Selected Module Introduction</label>
                <textarea className="form-textarea" rows={3} value={selectedModule.introduction || ""} onChange={(e) => setModules((prev) => prev.map((m) => (m.id === selectedModule.id ? { ...m, introduction: e.target.value } : m)))} />
              </div>
              <div className="form-row">
                <button className="btn btn-primary" onClick={handleUpdateModule} disabled={saving}>Save Module Update</button>
                <button className="btn btn-danger" onClick={handleDeleteModule} disabled={saving}>Delete Module</button>
              </div>
            </>
          ) : <p className="form-hint">Select a module to edit it.</p>}
        </div>
        )}

        {activeSection === "topics" && (
        <div className="edit-column">
          <h2>3. Topics</h2>
          {!selectedModule ? (
            <p className="form-hint">Select a module in the Modules tab first.</p>
          ) : (
            <>
              <div className="form-group">
                <label>Topic Title</label>
                <input className="form-input" value={topicDraft.title} onChange={(e) => setTopicDraft((p) => ({ ...p, title: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Topic Description</label>
                <textarea className="form-textarea" rows={2} value={topicDraft.description} onChange={(e) => setTopicDraft((p) => ({ ...p, description: e.target.value }))} />
              </div>
              <button className="btn btn-outline w-full" onClick={handleAddTopic} disabled={saving}>Add Topic</button>

              <div className="topic-list" style={{ marginTop: 12 }}>
                {topics.map((t) => (
                  <div key={t.id} className={`topic-card ${selectedTopicId === t.id ? "selected" : ""}`} onClick={async () => { setSelectedTopicId(t.id); await refreshLessons(t.id); }}>
                    <strong>{t.title}</strong>
                    <p>{t.description || "No description"}</p>
                  </div>
                ))}
              </div>

              {selectedTopic && (
                <>
                  <div className="form-group" style={{ marginTop: 12 }}>
                    <label>Edit Selected Topic Title</label>
                    <input className="form-input" value={selectedTopic.title || ""} onChange={(e) => setTopics((prev) => prev.map((t) => (t.id === selectedTopic.id ? { ...t, title: e.target.value } : t)))} />
                  </div>
                  <div className="form-group">
                    <label>Edit Selected Topic Description</label>
                    <textarea className="form-textarea" rows={2} value={selectedTopic.description || ""} onChange={(e) => setTopics((prev) => prev.map((t) => (t.id === selectedTopic.id ? { ...t, description: e.target.value } : t)))} />
                  </div>
                  <div className="form-row">
                    <button className="btn btn-primary" onClick={handleUpdateTopic} disabled={saving}>Save Topic Update</button>
                    <button className="btn btn-danger" onClick={handleDeleteTopic} disabled={saving}>Delete Topic</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        )}

        {activeSection === "lessons" && (
        <div className="edit-column">
          <h2>4. Language Lessons</h2>
          {!selectedTopic ? (
            <p className="form-hint">Select a topic in the Topics tab first.</p>
          ) : (
            <>
              <div className="form-group">
                <label>Language ID</label>
                <select
                  className="form-select"
                  value={lessonDraft.language_id}
                  onChange={(e) =>
                    setLessonDraft((p) => ({ ...p, language_id: e.target.value }))
                  }
                >
                  <option value="">Select language</option>
                  {languages.map((language) => (
                    <option key={language.id} value={String(language.id)}>
                      {language.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Lesson Content</label>
                <textarea className="form-textarea" rows={4} value={lessonDraft.topic_content} onChange={(e) => setLessonDraft((p) => ({ ...p, topic_content: e.target.value }))} />
              </div>

              <div className="form-group">
                <label>Media Uploads</label>
                <div className="media-upload-grid">
                  <label className="icon-upload-btn">
                    <input className="hidden-input" type="file" accept="image/*" onChange={(e) => setLessonDraft((p) => ({ ...p, topic_image: e.target.files?.[0] || null }))} />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
                    <span>Image</span>
                  </label>
                  <label className="icon-upload-btn">
                    <input className="hidden-input" type="file" accept="audio/*" onChange={(e) => setLessonDraft((p) => ({ ...p, audio: e.target.files?.[0] || null }))} />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /></svg>
                    <span>Audio</span>
                  </label>
                  <label className="icon-upload-btn">
                    <input className="hidden-input" type="file" accept="video/*" onChange={(e) => setLessonDraft((p) => ({ ...p, video: e.target.files?.[0] || null }))} />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
                    <span>Video</span>
                  </label>
                  <label className="icon-upload-btn">
                    <input className="hidden-input" type="file" onChange={(e) => setLessonDraft((p) => ({ ...p, document: e.target.files?.[0] || null }))} />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
                    <span>Document</span>
                  </label>
                </div>
                <div className="selected-files-list">
                  {lessonDraft.topic_image?.name ? <p className="form-hint">Image: {lessonDraft.topic_image.name}</p> : null}
                  {lessonDraft.audio?.name ? <p className="form-hint">Audio: {lessonDraft.audio.name}</p> : null}
                  {lessonDraft.video?.name ? <p className="form-hint">Video: {lessonDraft.video.name}</p> : null}
                  {lessonDraft.document?.name ? <p className="form-hint">Document: {lessonDraft.document.name}</p> : null}
                </div>
              </div>

              <button className="btn btn-primary w-full" onClick={handleAddOrUpdateLesson} disabled={saving}>{selectedLessonId ? "Update Lesson" : "Add Lesson"}</button>

              <div className="topic-list" style={{ marginTop: 12 }}>
                {lessons.map((lesson) => (
                  <div key={lesson.id} className="topic-card">
                    <strong>Language #{lesson.language_id || "N/A"}</strong>
                    <p>{(lesson.topic_content || "").slice(0, 120) || "No text"}</p>
                    <div className="form-row">
                      <button className="btn btn-outline" onClick={() => handleEditLesson(lesson)} disabled={saving}>Edit</button>
                      <button className="btn btn-danger" onClick={() => handleDeleteLesson(lesson.id)} disabled={saving}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default CreateCourse;
